import React, { useState, useRef, useMemo, useEffect } from 'react';
import { FormField, Input, Select } from '../ui/Form';
import { Button } from '../ui/Button';
import { Mail, Shield, Upload, X, MapPin, Store, Building2, Phone, Briefcase } from 'lucide-react';
import type { UserWithDetails } from '../../hooks/useUsers';
import { AVAILABLE_ROLES } from '../../config/organization';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';
import type { CreatableUserRole } from '../../types/auth';

/**
 * Données du formulaire alignées avec le backend CreateUserDto/InviteUserDto
 */
export interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: CreatableUserRole;
  phone?: string;
  position?: string;
  permissions?: string[];
  // Unité d'affaires
  businessUnitId?: string;
  businessUnitType?: 'company' | 'branch' | 'pos';
  // Avatar (géré côté frontend)
  avatar?: string;
}

interface UserFormProps {
  user?: UserWithDetails;
  onSubmit: (data: UserFormData) => Promise<void>;
  onCancel: () => void;
}

export function UserForm({ user, onSubmit, onCancel }: UserFormProps) {
  const { hierarchy, loadHierarchy } = useBusinessUnit();
  
  // Charger la hiérarchie au montage du formulaire pour avoir les succursales/POS
  useEffect(() => {
    loadHierarchy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState<string | undefined>(user?.avatar);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Extraire firstName/lastName depuis le nom complet si on édite un utilisateur existant
  const extractedNames = useMemo(() => {
    if (!user?.name) return { firstName: '', lastName: '' };
    const parts = user.name.split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || ''
    };
  }, [user?.name]);

  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || extractedNames.firstName,
    lastName: user?.lastName || extractedNames.lastName,
    role: (user?.role as CreatableUserRole) || 'viewer',
    phone: user?.phone || '',
    position: user?.position || '',
    // Unité d'affaires - mapping depuis l'ancien format
    businessUnitType: (user?.businessUnitType || user?.accessLevel || 'company') as 'company' | 'branch' | 'pos',
    businessUnitId: user?.businessUnitId || user?.primaryBusinessUnitId || ''
  });

  // Options de rôles disponibles pour la création d'utilisateurs
  // Alignés avec le backend: accountant, manager, analyst, audit, viewer
  const roleOptions = AVAILABLE_ROLES.map(role => ({
    value: role.value,
    label: role.label
  }));

  // Options de niveau d'accès (type d'unité d'affaires)
  const businessUnitTypeOptions = [
    { value: 'company', label: 'Entreprise générale (tous les accès)' },
    { value: 'branch', label: 'Succursale spécifique' },
    { value: 'pos', label: 'Point de vente spécifique' }
  ];

  // Options d'unités commerciales selon le type sélectionné
  const businessUnitOptions = useMemo(() => {
    if (!hierarchy) return [];
    
    switch (formData.businessUnitType) {
      case 'company':
        return [{ value: hierarchy.company.id, label: hierarchy.company.name }];
      case 'branch':
        return hierarchy.branches.map(branch => ({
          value: branch.id,
          label: `${branch.name}${branch.city ? ` (${branch.city})` : ''}`
        }));
      case 'pos':
        return hierarchy.pointsOfSale.map(pos => {
          const branch = hierarchy.branches.find(b => b.id === pos.branchId);
          return {
            value: pos.id,
            label: branch ? `${pos.name} - ${branch.name}` : pos.name
          };
        });
      default:
        return [];
    }
  }, [hierarchy, formData.businessUnitType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type et la taille
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('La taille de l\'image ne doit pas dépasser 5MB');
      return;
    }

    // Créer une prévisualisation
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatar(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation des champs requis
    if (!formData.email || !formData.firstName || !formData.lastName) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        phone: formData.phone || undefined,
        position: formData.position || undefined,
        businessUnitType: formData.businessUnitType,
        businessUnitId: formData.businessUnitId || undefined,
        avatar,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* En-tête avec photo de profil */}
      <div className="flex items-center gap-6 pb-6 border-b border-border">
        <div className="relative flex-shrink-0">
          {avatar ? (
            <div className="relative">
              <img
                src={avatar}
                alt="Avatar"
                className="w-20 h-20 rounded-full object-cover border-2 border-border"
              />
              <button
                type="button"
                onClick={() => setAvatar(undefined)}
                className="absolute -top-1 -right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 shadow-sm"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
              <Upload className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-foreground mb-1">Photo de profil</h3>
          <p className="text-xs text-muted-foreground mb-2">JPG, PNG ou GIF. Max 5MB.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            {avatar ? 'Changer' : 'Télécharger'}
          </Button>
        </div>
      </div>

      {/* Informations personnelles */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Informations personnelles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Prénom" required>
            <Input
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder="Jean"
            />
          </FormField>

          <FormField label="Nom" required>
            <Input
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder="Dupont"
            />
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="jean.dupont@entreprise.com"
              icon={Mail}
              disabled={!!user}
            />
          </FormField>

          <FormField label="Téléphone">
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+243999999999"
              icon={Phone}
            />
          </FormField>
        </div>
      </div>

      {/* Rôle et position */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Rôle et fonction</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Rôle" required>
            <Select
              value={formData.role}
              onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value as CreatableUserRole }))}
              options={roleOptions}
              icon={Shield}
            />
          </FormField>

          <FormField label="Position/Fonction">
            <Input
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              placeholder="Comptable principal"
              icon={Briefcase}
            />
          </FormField>
        </div>
        
        {/* Description du rôle sélectionné */}
        {formData.role && (
          <div className="mt-3 p-3 bg-muted/50 rounded-md">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{AVAILABLE_ROLES.find(r => r.value === formData.role)?.label}:</span>{' '}
              {AVAILABLE_ROLES.find(r => r.value === formData.role)?.description}
            </p>
          </div>
        )}
      </div>

      {/* Accès aux unités commerciales */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          Périmètre d'accès
        </h3>
        
        <div className="space-y-4">
          <FormField label="Type d'unité d'affaires" required>
            <Select
              value={formData.businessUnitType}
              onChange={(e) => {
                const newType = e.target.value as 'company' | 'branch' | 'pos';
                setFormData(prev => ({ 
                  ...prev, 
                  businessUnitType: newType,
                  businessUnitId: newType === 'company' && hierarchy ? hierarchy.company.id : ''
                }));
              }}
              options={businessUnitTypeOptions}
              icon={formData.businessUnitType === 'company' ? Building2 : formData.businessUnitType === 'branch' ? MapPin : Store}
            />
          </FormField>

          {formData.businessUnitType !== 'company' && (
            <FormField 
              label={formData.businessUnitType === 'branch' ? 'Succursale assignée' : 'Point de vente assigné'} 
              required
            >
              <Select
                value={formData.businessUnitId}
                onChange={(e) => setFormData(prev => ({ ...prev, businessUnitId: e.target.value }))}
                options={[
                  { value: '', label: `Sélectionner ${formData.businessUnitType === 'branch' ? 'une succursale' : 'un point de vente'}` },
                  ...businessUnitOptions
                ]}
                icon={formData.businessUnitType === 'branch' ? MapPin : Store}
              />
            </FormField>
          )}

          {/* Info box */}
          <div className="flex items-start gap-3 p-3 bg-background rounded-md border border-border">
            <div className="flex-shrink-0 mt-0.5">
              {formData.businessUnitType === 'company' && <Building2 className="h-4 w-4 text-blue-500" />}
              {formData.businessUnitType === 'branch' && <MapPin className="h-4 w-4 text-green-500" />}
              {formData.businessUnitType === 'pos' && <Store className="h-4 w-4 text-purple-500" />}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {formData.businessUnitType === 'company' && 
                "Accès complet à toutes les données de l'entreprise, incluant toutes les succursales et points de vente."}
              {formData.businessUnitType === 'branch' && 
                "Accès limité aux données de la succursale assignée et de ses points de vente uniquement."}
              {formData.businessUnitType === 'pos' && 
                "Accès restreint aux données du point de vente assigné uniquement."}
            </p>
          </div>
        </div>
      </div>

      {/* Note sur l'invitation */}
      {!user && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Note:</strong> Un email d'invitation sera envoyé à l'utilisateur pour qu'il définisse son mot de passe et active son compte.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          isLoading={loading}
        >
          {user ? 'Enregistrer les modifications' : 'Inviter l\'utilisateur'}
        </Button>
      </div>
    </form>
  );
}